import { useState } from 'react'
import clsx from 'clsx'
import { useFormField } from '@hooks/useFormField'
import { RichTextEditor, type EditorProps } from '@atoms/form/RichTextEditor'
import { type BaseFormFieldProps } from '@atoms/form/TextInput'
import LoadingWrapper from './LoadingWrapper'

export type RichTextEditorFieldProps = BaseFormFieldProps & 
  Omit<EditorProps, 'value' | 'onChange' | 'onBlur'> & {
  value?: string
  onChange?: (value: string) => void
  onBlur?: () => void
  description?: string
}

export const RichTextEditorField = (props: RichTextEditorFieldProps) => {
  const {
    name,
    label,
    description,
    required,
    errors,
    dataCompId,
    value,
    onChange,
    onBlur,
    placeholder,
    disabled,
    height = 250,
    minHeight = 250,
    maxHeight = 800,
    ...editorProps
  } = props

  const { fieldError, errorId, requiredText } = useFormField(
    name, 
    errors, 
    required
  )

  const [editorLoading, setEditorLoading] = useState(true)

  return (
    <LoadingWrapper loading={editorLoading}>
      <div
        className="tw:flex tw:flex-col tw:justify-center tw:min-w-0 tw:w-full"
        {...(dataCompId && { 'data-compid': dataCompId })}
      >
        <label
          htmlFor={name}
          className={clsx(
            'tw:mb-0 tw:pb-2 tw:text-sm tw:text-gray-700 tw:font-medium tw:block',
            fieldError && 'tw:text-red-600'
          )}
        >
          {label} {requiredText}
        </label>
        {description && <p className="tw:mb-2 tw:text-sm tw:text-gray-cool-600">{description}</p>}

        <RichTextEditor
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          setIsLoading={setEditorLoading}
          isLoading={editorLoading}
          placeholder={placeholder}
          disabled={disabled}
          height={height}
          minHeight={minHeight}
          maxHeight={maxHeight}
          hasError={!!fieldError}
          className={clsx(
            disabled && 'tw:opacity-60 tw:cursor-not-allowed'
          )}
          dataCompId={dataCompId}
          {...editorProps}
        />

        <div role="status" aria-relevant="all">
          {fieldError && (
            <p id={errorId} className="tw:text-red-600 tw:font-light tw:text-sm tw:pt-1">
              {fieldError.message}
            </p>
          )}
        </div>
      </div>
    </LoadingWrapper>
  )
}

RichTextEditorField.displayName = 'RichTextEditorField' 